// src/routes/About.jsx
import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  FaArrowRight,
  FaHandshake,
  FaUsers,
  FaGem,
  FaChartBar,
} from "react-icons/fa";

gsap.registerPlugin(ScrollTrigger);

/*
  ABOUT US — THE STUDIO.
  The trust page: professional, but human. Centerpiece is "the letter" —
  a personal note that brightens line by line as you scroll through it.
  Around it: real numbers, how we work, the person you'll talk to, and
  the brands that trust us.
  NOTE for Amiri: everything marked EDIT below needs your real details
  (founder bio + photo, timeline years). All other copy is editable too.
*/

const STATS = [
  { value: 20, suffix: "+", label: "brands built & grown" },
  { value: 50, suffix: "M+", label: "views for our clients" },
  { value: 3, suffix: "", label: "markets we call home" },
  { value: 100, suffix: "%", label: "of the work done in-house" },
];

const VALUES = [
  {
    Icon: FaHandshake,
    title: "Honesty over hype",
    body: "If we think an idea won't work, we say so before you spend a euro on it. We'd rather lose a project than sell you something we don't believe in.",
    accent: "#9281dd",
  },
  {
    Icon: FaUsers,
    title: "Small on purpose",
    body: "No account managers, no telephone game. When you work with us, you talk directly to the people designing, filming, and coding your project.",
    accent: "#e6608a",
  },
  {
    Icon: FaGem,
    title: "Obsessed with craft",
    body: "The animation nobody asked for. The hook rewritten eleven times. The detail you can't name but can feel. That's the standard, every time.",
    accent: "#4ea9d6",
  },
  {
    Icon: FaChartBar,
    title: "Results you can screenshot",
    body: "Views, orders, bookings — everything we make is measured. If it doesn't move a number you care about, we change it until it does.",
    accent: "#f2b134",
  },
];

const LETTER = [
  "We started ASites because we were tired of watching good businesses get invisible.",
  "Generic websites. Content nobody stops for. Agencies that talk in reports instead of results.",
  "So we built the studio we wished existed — one team that writes the hook, films the video, designs the brand, and codes the website. Under one roof, obsessing over every detail.",
  "We keep our client list short on purpose. Fewer brands, deeper work, real relationships.",
  "And we'll always tell you the truth — even when it costs us the sale.",
];

// EDIT: swap these for your real milestones & years
const TIMELINE = [
  { year: "The start", text: "ASites begins as one laptop and an obsession with making local brands look world-class." },
  { year: "First viral hit", text: "A client video crosses a million views organically. We realize the hook is everything — and double down on content." },
  { year: "Beyond posts", text: "We expand into full web development: cinematic React sites, e-commerce, booking systems built in-house." },
  { year: "Three markets", text: "Kosovo, Albania, Macedonia. Different audiences, one standard of work." },
  { year: "Today", text: "A full-stack creative studio: web, content, marketing, systems, branding — for brands that refuse to blend in." },
];

const CLIENTS = [
  "Orhidea Perfume",
  "Pretty Needs",
  "RRUSH",
  "OrgaLife",
  "Travelo",
  "Jara Boutique",
  "Coolest Parfume",
  "Dot's Coffee",
  "Urban RenderX",
  "Pro Interior TG",
];

const About = () => {
  const heroWordRefs = useRef([]);
  const letterRefs = useRef([]);
  const letterSecRef = useRef(null);
  const statRefs = useRef([]);
  const lineRef = useRef(null);
  const timelineRef = useRef(null);
  const [revealed, setRevealed] = useState({});

  /* hero words rise on mount — plays unconditionally, always completes */
  useEffect(() => {
    window.scrollTo(0, 0);
    gsap.fromTo(
      heroWordRefs.current,
      { yPercent: 110 },
      {
        yPercent: 0,
        stagger: 0.08,
        duration: 1,
        ease: "power4.out",
        delay: 0.15,
      }
    );
  }, []);

  /* the letter reads itself: lines brighten as they pass the viewport.
     Base color is already readable — worst case it's simply a letter. */
  useEffect(() => {
    const ctx = gsap.context(() => {
      letterRefs.current.forEach((el) => {
        if (!el) return;
        gsap.fromTo(
          el,
          { color: "#5d5770" },
          {
            color: "#ffffff",
            ease: "none",
            scrollTrigger: {
              trigger: el,
              start: "top 75%",
              end: "top 45%",
              scrub: true,
            },
          }
        );
      });

      // timeline thread draws itself
      const line = lineRef.current;
      if (line) {
        const len = line.getTotalLength();
        gsap.set(line, { strokeDasharray: len, strokeDashoffset: len });
        gsap.to(line, {
          strokeDashoffset: 0,
          ease: "none",
          scrollTrigger: {
            trigger: timelineRef.current,
            start: "top 65%",
            end: "bottom 75%",
            scrub: 1,
          },
        });
      }
    });
    return () => ctx.revert();
  }, []);

  /* IO-driven reveals + stat count-ups (pin-proof, collision-proof) */
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const key = e.target.dataset.reveal;
          setRevealed((r) => (r[key] ? r : { ...r, [key]: true }));

          // stat count-up
          if (e.target.dataset.stat !== undefined) {
            const i = Number(e.target.dataset.stat);
            const node = statRefs.current[i];
            if (node && !node.dataset.counted) {
              node.dataset.counted = "1";
              const obj = { v: 0 };
              gsap.to(obj, {
                v: STATS[i].value,
                duration: 1.6,
                ease: "power3.out",
                onUpdate: () => {
                  node.textContent = `${Math.round(obj.v)}${STATS[i].suffix}`;
                },
              });
            }
          }
          io.unobserve(e.target);
        });
      },
      { threshold: 0.25 }
    );

    document.querySelectorAll("[data-reveal]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <Page>
      {/* ── HERO ── */}
      <Hero>
        <div className="au-aura" />
        <span className="au-kicker">ABOUT US — THE STUDIO</span>
        <h1>
          {["Small", "studio.", "Obsessive", "standards."].map((w, i) => (
            <span className="au-mask" key={i}>
              <span
                className={`au-w ${i >= 2 ? "hl" : ""}`}
                ref={(el) => (heroWordRefs.current[i] = el)}
              >
                {w}
              </span>
            </span>
          ))}
        </h1>
        <p className="au-sub">
          We're ASites — a web & marketing studio from Pristina, building
          brands people can't ignore across Kosovo, Albania, and Macedonia.
        </p>
      </Hero>

      {/* ── THE LETTER ── */}
      <Letter ref={letterSecRef}>
        <span className="au-kicker center">A NOTE FROM US</span>
        {LETTER.map((line, i) => (
          <p key={i} ref={(el) => (letterRefs.current[i] = el)}>
            {line}
          </p>
        ))}
        <span className="au-sign">— the ASites team</span>
      </Letter>

      {/* ── NUMBERS ── */}
      <Stats>
        {STATS.map((s, i) => (
          <div
            className={`au-stat ${revealed[`stat${i}`] ? "in" : ""}`}
            key={i}
            data-reveal={`stat${i}`}
            data-stat={i}
            style={{ transitionDelay: `${i * 0.08}s` }}
          >
            <span className="au-value" ref={(el) => (statRefs.current[i] = el)}>
              0{s.suffix}
            </span>
            <span className="au-label">{s.label}</span>
          </div>
        ))}
      </Stats>

      {/* ── HOW WE WORK ── */}
      <Values>
        <div className="au-head">
          <span className="au-kicker">HOW WE WORK</span>
          <h2>
            What working with us <em>actually</em> feels like.
          </h2>
        </div>
        <div className="au-grid">
          {VALUES.map((v, i) => (
            <div
              className={`au-card ${revealed[`val${i}`] ? "in" : ""}`}
              key={v.title}
              data-reveal={`val${i}`}
              style={{ "--accent": v.accent, transitionDelay: `${i * 0.08}s` }}
            >
              <v.Icon className="au-ico" />
              <h3>{v.title}</h3>
              <p>{v.body}</p>
            </div>
          ))}
        </div>
      </Values>

      {/* ── THE JOURNEY ── */}
      <Journey ref={timelineRef}>
        <div className="au-head center">
          <span className="au-kicker">THE JOURNEY</span>
          <h2>
            How we got <em>here.</em>
          </h2>
        </div>

        <div className="au-thread-wrap">
          <svg
            className="au-thread"
            preserveAspectRatio="none"
            viewBox="0 0 10 1000"
            aria-hidden="true"
          >
            <path
              ref={lineRef}
              d="M5 0 V1000"
              stroke="url(#au-grad)"
              strokeWidth="2"
              fill="none"
            />
            <defs>
              <linearGradient id="au-grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#9281dd" />
                <stop offset="100%" stopColor="#b9c5fb" />
              </linearGradient>
            </defs>
          </svg>

          {TIMELINE.map((t, i) => (
            <div
              className={`au-milestone ${i % 2 ? "right" : "left"} ${
                revealed[`tl${i}`] ? "in" : ""
              }`}
              key={i}
              data-reveal={`tl${i}`}
            >
              <span className="au-year">{t.year}</span>
              <p>{t.text}</p>
            </div>
          ))}
        </div>
      </Journey>

      {/* ── THE PERSON YOU'LL TALK TO ── */}
      <Founder>
        <div
          className={`au-founder-card ${revealed.founder ? "in" : ""}`}
          data-reveal="founder"
        >
          <div className="au-photo">
            {/* EDIT: drop your photo here —
                <img src={require("../assets/amiri.jpg")} alt="Amiri" /> */}
            <span className="au-photo-ph">A</span>
          </div>
          <div className="au-founder-txt">
            <span className="au-kicker">THE PERSON YOU'LL TALK TO</span>
            <h2>Hi, I'm Amiri.</h2>
            <p>
              I founded ASites and I'm still in every project — writing hooks,
              reviewing designs, and pushing the code that ships. When you
              message us, you're not entering a ticket queue. You're talking
              to the person responsible for your results.
              {/* EDIT: personalize this bio with your real story */}
            </p>
            <a className="au-founder-btn" href="/#contact">
              Say hello <FaArrowRight />
            </a>
          </div>
        </div>
      </Founder>

      {/* ── TRUSTED BY ── */}
      <Trusted>
        <span className="au-kicker center">BRANDS THAT TRUST US</span>
        <div className="au-marquee">
          <div className="au-track">
            {[...CLIENTS, ...CLIENTS].map((c, i) => (
              <span className="au-client" key={i}>
                {c} <i>✦</i>
              </span>
            ))}
          </div>
        </div>
      </Trusted>

      {/* ── CLOSING ── */}
      <Closing>
        <h2>
          Let's <em>meet.</em>
        </h2>
        <p>
          The first call is just a conversation — no pitch decks, no pressure.
          Tell us what you're building and we'll tell you honestly if we can
          help.
        </p>
        <a className="au-btn" href="/#contact">
          Start the conversation <FaArrowRight />
        </a>
      </Closing>
    </Page>
  );
};

/* ─────────────────────────── STYLES ─────────────────────────── */

const scroll = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;

const Page = styled.div`
  background: #08040c;
  color: #ffffff;
  font-family: "Readex Pro", sans-serif;

  .au-kicker {
    font-family: "Montserrat";
    letter-spacing: 0.45em;
    font-size: 0.72rem;
    color: #836caf;
    font-weight: 500;
    &.center {
      display: block;
      text-align: center;
    }
  }

  .au-head {
    margin-bottom: 3em;
    &.center {
      text-align: center;
    }
    h2 {
      font-weight: 200;
      font-size: clamp(2rem, 5vw, 4.2rem);
      line-height: 1;
      margin-top: 0.35em;
      em {
        font-style: italic;
        color: #9281dd;
      }
    }
  }
`;

const Hero = styled.section`
  position: relative;
  min-height: 88vh;
  padding: 12em 7% 6em;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;

  .au-aura {
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 30% 0%, #33224d55 0%, #08040c 55%);
    pointer-events: none;
  }

  h1 {
    position: relative;
    font-weight: 200;
    font-size: clamp(2.6rem, 8vw, 7.5rem);
    line-height: 1.04;
    margin-top: 0.35em;
    display: flex;
    flex-wrap: wrap;
    gap: 0 0.28em;
    max-width: 12ch;

    .au-mask {
      display: inline-block;
      overflow: hidden;
    }
    .au-w {
      display: inline-block;
      will-change: transform;
    }
    .hl {
      color: #9281dd;
      font-style: italic;
    }
  }

  .au-sub {
    position: relative;
    color: #b3aec2;
    margin-top: 1.6em;
    max-width: 52ch;
    font-family: "Montserrat";
    font-size: clamp(0.95rem, 1.4vw, 1.15rem);
    line-height: 1.65;
  }

  @media (max-width: 700px) {
    padding: 9em 6% 4em;
    min-height: 70vh;
  }
`;

const Letter = styled.section`
  position: relative;
  padding: 8em 7%;
  max-width: 860px;
  margin: 0 auto;

  p {
    font-weight: 300;
    font-size: clamp(1.3rem, 2.8vw, 2.1rem);
    line-height: 1.5;
    margin-top: 1.4em;
    color: #5d5770; /* readable base — the scroll only brightens it */
  }

  .au-sign {
    display: block;
    margin-top: 2.2em;
    font-family: "Montserrat";
    font-size: 0.95rem;
    color: #9281dd;
    letter-spacing: 0.05em;
  }
`;

const Stats = styled.section`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: clamp(2em, 7vw, 6em);
  padding: 5em 7%;
  border-top: 1px solid #170f26;
  border-bottom: 1px solid #170f26;

  .au-stat {
    text-align: center;
    opacity: 0;
    transform: translateY(30px);
    transition: opacity 0.8s ease, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
    &.in {
      opacity: 1;
      transform: translateY(0);
    }
    .au-value {
      display: block;
      font-weight: 200;
      font-size: clamp(2.4rem, 6vw, 4.6rem);
      line-height: 1;
      background: linear-gradient(180deg, #ffffff, #b9c5fb);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .au-label {
      color: #8a8595;
      font-size: 0.88rem;
      font-family: "Montserrat";
    }
  }
`;

const Values = styled.section`
  padding: 8em 7%;

  .au-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(270px, 100%), 1fr));
    gap: 1.2em;
  }

  .au-card {
    border: 1px solid #1d1530;
    border-radius: 18px;
    padding: 2em 1.7em;
    background: #0d0917;
    opacity: 0;
    transform: translateY(34px);
    transition: opacity 0.8s ease, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1),
      border-color 0.4s ease, box-shadow 0.4s ease;
    &.in {
      opacity: 1;
      transform: translateY(0);
    }
    &:hover {
      border-color: var(--accent);
      box-shadow: 0 24px 60px -30px var(--accent);
    }

    .au-ico {
      font-size: 1.6rem;
      color: var(--accent);
    }
    h3 {
      font-weight: 300;
      font-size: 1.35rem;
      margin: 0.8em 0 0.5em;
    }
    p {
      color: #9d97ab;
      font-size: 0.94rem;
      line-height: 1.65;
      font-family: "Montserrat";
    }
  }
`;

const Journey = styled.section`
  padding: 6em 7% 8em;

  .au-thread-wrap {
    position: relative;
    max-width: 900px;
    margin: 0 auto;
  }

  .au-thread {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 10px;
    height: 100%;
    pointer-events: none;
  }

  .au-milestone {
    position: relative;
    width: 46%;
    margin-bottom: 4.5em;
    opacity: 0;
    transition: opacity 0.8s ease, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);

    &.left {
      margin-right: auto;
      text-align: right;
      transform: translateX(-26px);
    }
    &.right {
      margin-left: auto;
      text-align: left;
      transform: translateX(26px);
    }
    &.in {
      opacity: 1;
      transform: translateX(0);
    }

    .au-year {
      font-family: "Montserrat";
      font-size: 0.72rem;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: #9281dd;
      font-weight: 600;
    }
    p {
      margin-top: 0.5em;
      color: #b3aec2;
      font-size: clamp(0.92rem, 1.3vw, 1.05rem);
      line-height: 1.6;
    }
  }

  @media (max-width: 700px) {
    .au-thread {
      left: 4px;
      transform: none;
    }
    .au-milestone {
      width: 100%;
      padding-left: 28px;
      text-align: left !important;
      margin-bottom: 3em;
    }
  }
`;

const Founder = styled.section`
  padding: 4em 7% 8em;

  .au-founder-card {
    max-width: 900px;
    margin: 0 auto;
    display: flex;
    gap: 3em;
    align-items: center;
    border: 1px solid #1d1530;
    border-radius: 24px;
    padding: 3em;
    background: radial-gradient(circle at 0% 0%, #1a1128 0%, #0d0917 60%);
    opacity: 0;
    transform: translateY(34px);
    transition: opacity 0.8s ease, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
    &.in {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .au-photo {
    flex: 0 0 clamp(140px, 22vw, 220px);
    aspect-ratio: 1;
    border-radius: 20px;
    overflow: hidden;
    background: linear-gradient(140deg, #2a1d40, #171028);
    border: 1px solid #2a2140;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .au-photo-ph {
      font-size: clamp(3rem, 8vw, 5.5rem);
      font-weight: 200;
      color: #9281dd;
    }
  }

  .au-founder-txt {
    h2 {
      font-weight: 200;
      font-size: clamp(1.8rem, 4vw, 3rem);
      margin: 0.35em 0 0.4em;
    }
    p {
      color: #b3aec2;
      font-family: "Montserrat";
      font-size: clamp(0.92rem, 1.3vw, 1.02rem);
      line-height: 1.7;
      max-width: 52ch;
    }
    .au-founder-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.6em;
      margin-top: 1.6em;
      font-family: "Montserrat";
      font-weight: 600;
      font-size: 0.95rem;
      color: #9281dd;
      text-decoration: none;
      border-bottom: 1px solid #9281dd55;
      padding-bottom: 3px;
      transition: 0.3s;
      svg {
        transition: transform 0.3s ease;
      }
      &:hover {
        color: #b9c5fb;
        border-color: #b9c5fb;
        svg {
          transform: translateX(4px);
        }
      }
    }
  }

  @media (max-width: 760px) {
    .au-founder-card {
      flex-direction: column;
      text-align: center;
      padding: 2.2em 1.6em;
      .au-founder-txt p {
        max-width: none;
      }
    }
  }
`;

const Trusted = styled.section`
  padding: 3em 0 6em;
  overflow: hidden;

  .au-marquee {
    margin-top: 2em;
    overflow: hidden;
    mask-image: linear-gradient(
      90deg,
      transparent,
      #000 12%,
      #000 88%,
      transparent
    );
    -webkit-mask-image: linear-gradient(
      90deg,
      transparent,
      #000 12%,
      #000 88%,
      transparent
    );
  }

  .au-track {
    display: flex;
    width: max-content;
    animation: ${scroll} 32s linear infinite;
  }

  .au-client {
    white-space: nowrap;
    font-weight: 200;
    font-size: clamp(1.4rem, 3vw, 2.4rem);
    color: #6f6a7c;
    padding: 0 0.9em;
    i {
      font-style: normal;
      color: #33294d;
      font-size: 0.55em;
      margin-left: 1.4em;
      vertical-align: middle;
    }
  }
`;

const Closing = styled.section`
  text-align: center;
  padding: 8em 7% 9em;
  background: radial-gradient(
    circle at 50% 100%,
    #221537 0%,
    #100818 45%,
    #08040c 75%
  );

  h2 {
    font-weight: 200;
    font-size: clamp(2.6rem, 8vw, 6.5rem);
    line-height: 1;
    em {
      font-style: italic;
      color: #9281dd;
    }
  }
  p {
    color: #b3aec2;
    max-width: 46ch;
    margin: 1.4em auto 2.4em;
    font-family: "Montserrat";
    font-size: clamp(0.9rem, 1.3vw, 1.05rem);
    line-height: 1.65;
  }
  .au-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.7em;
    font-family: "Montserrat";
    font-weight: 600;
    font-size: 1rem;
    padding: 17px 36px;
    border-radius: 30px;
    text-decoration: none;
    background: linear-gradient(120deg, #9281dd, #b9c5fb);
    color: #100a18;
    transition: 0.35s;
    svg {
      transition: transform 0.35s ease;
    }
    &:hover {
      box-shadow: 0 0 36px -6px #9281dd;
      transform: translateY(-2px);
      svg {
        transform: translateX(4px);
      }
    }
  }
`;

export default About;
